import { Injectable } from '@angular/core';
import { RequestsProvider, TypeDataRequest } from '../requests/requests';
import { StorageProvider } from '../storage/storage';
import { NetworkProvider } from '../network/network';
import { LoggerWrapper } from '../logger/logger-wrapper.service';
import { LoggerFactory } from '../logger/logger-factory.service';

import moment from 'moment';
import { HttpErrorResponse } from '@angular/common/http';
import { UserService } from '../auth/user.service';
import { environment } from '../../../../environments/environment';
import { FormulaireTypes } from '../../../mnt/common/models/formulaires';
import { Events } from '../events/events.provider';
import { PostCrUnplanned } from '../../models/v2/post-cr.model';
import { OtDiffererDataDto } from '../../../api/models/ot-differer-data-dto';
import { OtControllerV2Api } from '../../../api/services/ot-controller-v-2-api';
import { DiffererOt$Params } from '../../../api/fn/ot-controller-v-2/differer-ot';
import { firstValueFrom } from 'rxjs';
import { OtResponse } from '../../../api/models/ot-response';
import { CrResponse } from '../../../api/models/cr-response';

/**
 * Activités Provider
 *
 * Ce service permet d'accéder aux service de l'API Activités
 */
@Injectable({ providedIn: 'root' })
export class ActivitesProvider {
  /**
   * Utilisé pour l'url de config de l'API Dactech Back
   *
   * @type {string}
   */
  apiDactechBackUrl = '';

  /**
   * Utilisé pour l'url de config de l'API Cr
   *
   * @type {string}
   */
  apiCrUrl = '';

  /**
   * Logger
   */
  private logger: LoggerWrapper;

  constructor(
    private requestsProvider: RequestsProvider,
    private userService: UserService,
    private storageProvider: StorageProvider,
    private events: Events,
    private networkProvider: NetworkProvider,
    private loggerFactory: LoggerFactory,
    private otControllerV2Api: OtControllerV2Api
  ) {
    this.logger = this.loggerFactory.getInstance(this);
    this.apiCrUrl = environment.apis.API_CR;
    this.apiDactechBackUrl = environment.apis.API_DACTECH_BACK_V1;
  }

  /**
   * Récupération de tous les ots (à faire ) par site.
   *
   * Note: cette fonction n'est disponible qu'en mode en-ligne.
   *
   * @returns Promise qui renvoie un array d'objets activité
   */
  async getOtsBySite(idSite: string): Promise<OtResponse[]> {
    try {
      let urlToCall =
        environment.apis.API_DACTECH_BACK_ROOT +
        '/v2/ots?idSite=' +
        idSite +
        '&statut=a_faire&limite=50&sortField=plan_date_fin&sortOrder=ASC';
      let ots: OtResponse[] = (await this.requestsProvider.getOffline(urlToCall, null, null, true)) as OtResponse[];
      if (ots == null) ots = [];
      return ots;
    } catch (err) {
      this.logger.warn('error getActivités', err);
      return [];
    }
  }

  /**
   * Récupération de tous les ots (à faire ) par site.
   *
   * Note: cette fonction n'est disponible qu'en mode en-ligne.
   *
   * @returns Promise qui renvoie un array d'objets activité
   */
  async getPlannedOtsBySite(
    idSite: string,
    afficherToutOtsSite: Boolean = true,
    team: Boolean = false,
    date_modification_debut?: string,
    date_modification_fin?: string
  ): Promise<OtResponse[]> {
    try {
      let urlToCall = environment.apis.API_DACTECH_BACK_ROOT + '/v2/ots?idSite=' + idSite + '&statut=a_faire';
      if (await this.networkProvider.isReallyConnected()) {
        urlToCall += '&limite=500';
      } else {
        // Nécessaire en offline ca l'Usr du GetInit est /ots?idSite=XXX&statut=a_faire&limite=50&sortField=plan_date_fin&sortOrder=ASC
        urlToCall += '&limite=50';
      }
      urlToCall += '&sortField=plan_date_fin&sortOrder=ASC';
      if (!afficherToutOtsSite || team) {
        let idUser = this.userService.getCurrentUser()['id'];
        urlToCall += '&idIntervenant=' + idUser;
      }
      if (team) {
        urlToCall += '&team=true';
      }
      if (date_modification_debut) urlToCall += '&planificationApresDate=' + date_modification_debut;
      if (date_modification_fin) urlToCall += '&planificationAvantDate=' + date_modification_fin;

      let ots: OtResponse[] = (await this.requestsProvider.getOffline(urlToCall, null, null, true)) as OtResponse[];
      if (ots == null) ots = [];
      return ots;
    } catch (err) {
      this.logger.warn('error getActivités', err);
      return [];
    }
  }

  /**
   * Récupération de tous les ots préventifs Orion et réglementaires par site aux statuts généré, préparé, analysé.
   *
   * @returns Promise qui renvoie un array d'objets activité
   */
  async getUnplannedOtsBySite(
    idSite: string,
    date_planification_previsionnelle_debut?: string,
    date_planification_previsionnelle_fin?: string
  ): Promise<OtResponse[]> {
    try {
      let ots: OtResponse[];
      let urlToCall = environment.apis.API_DACTECH_BACK_ROOT + '/v2/ots/unplanned_ots?idSite=' + idSite;
      if (date_planification_previsionnelle_debut)
        urlToCall += '&previsionDateDebut=' + date_planification_previsionnelle_debut;
      if (date_planification_previsionnelle_fin)
        urlToCall += '&previsionDateFin=' + date_planification_previsionnelle_fin;

      //each_doc = false parce que les donnees recuperees d'ot n'ont pas ot_reassignable rempli correctement
      ots = (await this.requestsProvider.getOffline(urlToCall, null, null, false)) as OtResponse[];
      if (ots == null) ots = [];
      return ots;
    } catch (err) {
      this.logger.warn('error getUnplannedOrionOtsBySite', err);
      return [];
    }
  }

  /**
   * Récupération de l'historique des OTs (clos intervenant ) par site
   *
   * @returns Promise qui renvoie un array d'objets activité
   */
  async getHistoryBySite(
    idSite: string,
    limite = 50,
    date_modification_debut: string = null,
    date_modification_fin: string = null
  ): Promise<OtResponse[]> {
    try {
      if (!(await this.networkProvider.isReallyConnected())) {
        limite = 20;
      }
      let urlToCall =
        environment.apis.API_DACTECH_BACK_ROOT +
        '/v2/ots?idSite=' +
        idSite +
        '&statut=clos_intervenant&statut=clos_valide&limite=' +
        limite +
        '';

      if (date_modification_debut != null && date_modification_debut != '')
        urlToCall += '&realisationApresDate=' + date_modification_debut;
      if (date_modification_fin != null && date_modification_fin != '')
        urlToCall += '&realisationAvantDate=' + date_modification_fin;

      return (await this.requestsProvider.getOffline(urlToCall, null, null, true)) as OtResponse[];
    } catch (err) {
      this.logger.warn('error getActivités', err);
      return [];
    }
  }

  /**
   * Récupération des champs pré-remplies du formulaire a partir d'un OT
   */
  async getActivityFormulaire(id, typeFormulaire: FormulaireTypes) {
    let activity_formulaire = null;
    try {
      activity_formulaire = await this.requestsProvider.getOfflineFirst<any>(
        this.apiDactechBackUrl + '/ots/' + id + '/formulaires/' + typeFormulaire + '/pre_rempli'
      );
    } catch (err) {
      this.logger.warn('error getActivityFormulaire', err);
    }
    return activity_formulaire;
  }

  /**
   * Récupération du détail d'une activité
   */
  async getOt(id: string): Promise<OtResponse | null> {
    try {
      return (await this.requestsProvider.getOfflineFirstWithLog<OtResponse>(
        environment.apis.API_DACTECH_BACK_ROOT + '/v2/ots/' + id,
        id
      )) as OtResponse;
    } catch (e) {
      console.log('Erreur: ', e);
      if (!(e instanceof HttpErrorResponse)) {
        this.logger.warn('error getActivity', e);
      }
    }

    return null;
  }

  /**
   * Récupération du détail d'une activité
   */
  async getActivity(id, idSite = null) {
    try {
      try {
        const ot = await this.requestsProvider.getOfflineFirst<any>(
          environment.apis.API_DACTECH_BACK_ROOT + '/v2/ots/' + id,
          id
        );
        if (ot != null) return this.transformActivity(ot);
      } catch (e) {
        console.log('Erreur: ', e);
        if (!(e instanceof HttpErrorResponse)) {
          throw e;
        }
      }
      // Si en offline et qu la premiere requête n'est pas en cache
      if (idSite == null) {
        throw new Error("Impossible de récuperer l'OT: " + id);
      }

      this.logger.log("Recherche de l'activité dans l'historique");
      let url =
        environment.apis.API_DACTECH_BACK_ROOT + '/v2/ots/search?idSite=' + idSite + '&historique=true&limite=20';
      const ots = await this.requestsProvider.getOfflineFirst<any>(url);
      if (ots != null) {
        const ot = ots.find((ot) => {
          return ot.id === id;
        });
        return ot;
      }
    } catch (err) {
      this.logger.warn('error getActivity', err);
    }
  }

  /**
   * Récupération du détail d'une activité
   */
  transformActivity(activity) {
    if (activity.localisations) {
      activity.localisations = this.transformActivityLocalisations(
        activity.localisations,
        activity.id_localisation_principale
      );
    }

    return activity;
  }

  /**
   * Récupération du détail d'une activité
   */
  transformActivityLocalisations(localisations, localisation_principale) {
    if (localisations.length > 0 && localisations[0] != null) {
      let id_localisation_principale = localisations[0].id;
      if (localisation_principale) id_localisation_principale = localisation_principale;
      localisations = localisations.find((localisation) => {
        return localisation.id == id_localisation_principale;
      });
    }
    return localisations;
  }

  async getOtCrs(id: string): Promise<{ status: 'not_found' | 'found'; crs: CrResponse[] }> {
    const apiCrUrlV2 = `${environment.apis.API_DACTECH_BACK_ROOT}/v2/crs`;
    if (await this.networkProvider.isReallyConnected()) {
      try {
        let crs: CrResponse[] = await this.requestsProvider.get(`${apiCrUrlV2}?id_ot=${id}`);
        if (crs) return { status: 'found', crs };
        else return { status: 'not_found', crs: [] };
      } catch (err) {
        this.logger.warn('error getActivityOtCr', err);
        return { status: 'not_found', crs: [] };
      }
    } else {
      let idUser = this.userService.getCurrentUser()['id'];
      let urlToCall = apiCrUrlV2 + '?id_intervenant=' + idUser + '';
      let storageName = this.storageProvider.getStorageName(urlToCall);
      let list_id_cr_intervenant = await this.storageProvider.get(storageName, true);
      try {
        let crs: CrResponse[] = list_id_cr_intervenant.filter((cr_list: CrResponse) => cr_list.id_ot === id);
        if (crs != null) {
          return { status: 'found', crs: crs };
        } else {
          return { status: 'not_found', crs: [] };
        }
      } catch (error) {
        this.logger.warn('error getActivity', error);
        return { status: 'not_found', crs: [] };
      }
    }
  }

  async tryToRefreshOt(idOt: string) {
    try {
      const ot = await this.requestsProvider.get(environment.apis.API_DACTECH_BACK_ROOT + '/v2/ots/' + idOt);
      await this.storageProvider.set(idOt, ot);

      let getMultiSitesGammesUrl = `${environment.apis.API_DACTECH_BACK_ROOT}/v2/ots/${idOt}/multi_sites_gammes`;
      let otFils: OtResponse[] = await this.requestsProvider.get(getMultiSitesGammesUrl);
      let multiGammeStorageName = this.requestsProvider.getStorageName(getMultiSitesGammesUrl);
      await this.storageProvider.set(multiGammeStorageName, otFils, false);

      if (otFils) {
        for (const otEnfant of otFils) {
          await this.storageProvider.set(otEnfant.id_ot, otEnfant);
        }
      }
    } catch (e) {
      this.logger.info("Impossible de rafraichir l'OT: " + idOt);
    }
  }

  /**
   * filtre des activités en fonction d'une localisation nested
   */
  activitesByNestedLoc(ots, nestedLoc) {
    let out = [];

    //this.logger.log(nestedLoc.libelle_court);

    let locOts = this.activitesByLocId(ots, nestedLoc.id);

    out = out.concat(locOts);

    if (nestedLoc.children) {
      nestedLoc.children.forEach((childLoc) => {
        let childLocOts = this.activitesByNestedLoc(ots, childLoc);

        out = out.concat(childLocOts);
      });
    }

    //this.logger.log(out);

    return out;
  }

  /**
   * filtre des activités en fonction d'une localisation
   */
  activitesByLocId(ots, locId) {
    return ots.filter((ot) => ot.localisations[0].id === locId);
  }

  /**
   * Ajout d'une nouveau CR
   */
  async getListCrByIntervenant(): Promise<CrResponse[]> {
    let idUser = this.userService.getCurrentUser()['id'];

    // Ajout d'un jour saisi_avant_date car borne exclusive
    try {
      if (await this.networkProvider.isReallyConnected()) {
        let urlToCall = `${environment.apis.API_DACTECH_BACK_ROOT}/v2/crs?id_intervenant=${idUser}&saisi_avant_date=${moment()
          .add(1, 'day')
          .toISOString()}&saisi_apres_date=${moment()
          .subtract(5, 'weeks')
          .subtract(1, 'day')
          .startOf('day')
          .toISOString()}`;
        return (await this.requestsProvider.getOffline(urlToCall)) as CrResponse[];
      } else {
        let urlToCall = `${environment.apis.API_DACTECH_BACK_ROOT}/v2/crs?id_intervenant=${idUser}`;
        return await this.storageProvider.get(this.storageProvider.getStorageName(urlToCall), true);
      }
    } catch (err) {
      this.logger.warn('error getActivity', err);
      return [];
    }
  }

  /**
   * Envoi d'une activité non planifié
   */
  async postActivityUnplanned(activite: PostCrUnplanned) {
    let id_intervenant = this.userService.getCurrentUser()['id'];
    this.events.publish('cr_list_update');
    return await this.requestsProvider.postOffline(
      // 'http://localhost:8092/v2/ots/create_unplanned_ot',
      environment.apis.API_DACTECH_BACK_ROOT + '/v2/ots/create_unplanned_ot',
      activite,
      null,
      `${this.apiCrUrl}?id_intervenant=${id_intervenant}`,
      null,
      TypeDataRequest.CRS_UNPLANNED
    );
  }

  async areCrDocumentsAvailable(idCr: string): Promise<boolean> {
    let url = `${environment.apis.API_DACTECH_BACK_ROOT}/v2/crs/${idCr}/areDocumentsAvailable`;
    try {
      return await this.requestsProvider.get(url);
    } catch (e) {
      return false;
    }
  }

  async differerOt(idOt: string, otDiffereDataDto: OtDiffererDataDto) {
    try {
      let param: DiffererOt$Params = {
        id_ot: idOt,
        body: otDiffereDataDto,
      };
      return await firstValueFrom(this.otControllerV2Api.differerOt(param));
    } catch (error) {
      return Promise.reject(error);
    }
  }
}
